import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Castle, Search } from "lucide-react";
import { Button } from "./Button";
import { cn } from "@/lib/utils";
import SearchDialog from "./SearchDialog";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Associazione", href: "/associazione" },
  { label: "Tesseramento", href: "/tesseramento" },
  { label: "Attività", href: "/attivita" },
  { label: "Castelli", href: "/castelli" },
  { label: "L'Aquila e Cecilia", href: "/laquila-cecilia" },
];

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // Keyboard shortcut per aprire la ricerca (Ctrl+K o Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const showSolid = true; // Always show solid navbar

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          "bg-surface/95 backdrop-blur-md shadow-elegant",
        )}
      >
        <div className="container-custom">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="/images/logo-header.png"
                srcSet="/images/logo-header.png 1x, /images/logo-header@2x.png 2x, /images/logo-header@3x.png 3x"
                alt="99 Castelli" 
                className="h-12 w-auto"
                width={155}
                height={48}
                decoding="async"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    "font-body font-medium transition-colors duration-200 relative",
                    "text-text hover:text-secondary",
                    location.pathname === link.href && "text-secondary",
                  )}
                >
                  {link.label}
                  {location.pathname === link.href && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-secondary"
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* Search + CTA Button - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Search Button */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-text-muted hover:text-foreground bg-muted/50 hover:bg-muted rounded-lg transition-colors border border-border/50"
                aria-label="Cerca"
              >
                <Search size={16} />
                <span className="hidden xl:inline">Cerca...</span>
                <kbd className="hidden xl:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-background rounded border border-border">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </button>

              <Button href="/tesseramento" variant="primary" size="sm">
                Diventa Socio
              </Button>
            </div>

            {/* Mobile: Search + Menu Button */}
            <div className="flex items-center gap-2 lg:hidden">
              {/* Search Button Mobile */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 text-foreground hover:text-secondary transition-colors"
                aria-label="Cerca"
              >
                <Search size={24} />
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 transition-colors text-foreground"
                aria-label={isMobileMenuOpen ? "Chiudi menu" : "Apri menu"}
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-primary/50 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-surface shadow-2xl z-50 lg:hidden"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <span className="text-xl font-heading font-bold text-foreground">Menu</span>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 text-foreground"
                    aria-label="Chiudi menu"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Search in Mobile Menu */}
                <div className="px-6 py-4 border-b border-border">
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsSearchOpen(true);
                    }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-left text-text-muted bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Search size={20} />
                    <span>Cerca nel sito...</span>
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto py-6">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.href}
                        className={cn(
                          "block px-6 py-4 font-body font-medium text-lg transition-colors",
                          location.pathname === link.href
                            ? "text-secondary bg-secondary/5"
                            : "text-foreground hover:text-secondary hover:bg-muted",
                        )}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </nav>

                <div className="p-6 border-t border-border">
                  <Button href="/tesseramento" variant="secondary" fullWidth size="lg">
                    Diventa Socio
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search Dialog */}
      <SearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
};

export default Navbar;
